import { useEffect, useMemo, useState } from 'react';
import { db } from '../../firebase';
import { addDoc, collection, doc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { logAudit } from '../../lib/audit';

const SupplierManagementView = ({ onBack, currentUser }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [debts, setDebts] = useState({}); // idProveedor -> valor pendiente
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Suscripción a proveedores activos (active !== false)
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'proveedores'), (ss) => {
      const list = ss.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.active !== false);
      setSuppliers(list);
    });
    return () => unsub();
  }, []);

  // Calcula deudas pendientes en segundo plano (simple y compatible)
  useEffect(() => {
    let cancel = false;
    (async () => {
      const result = {};
      for (const sup of suppliers) {
        try {
          const qRef = query(collection(db, 'entradas'), where('supplierId', '==', sup.id));
          const snap = await getDocs(qRef);
          const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          const pending = rows
            .filter(r => String(r.paymentStatus || '').toLowerCase() === 'pendiente')
            .reduce((s, r) => s + Number(r.totalEntryCost || 0), 0);
          result[sup.id] = pending;
        } catch (e) {
          result[sup.id] = 0;
        }
      }
      if (!cancel) setDebts(result);
    })();
    return () => { cancel = true; };
  }, [suppliers]);

  const emptyForm = useMemo(() => ({
    nombre: '',
    contacto: '',
    telefono: '',
    email: '',
    nit: '',
    direccion: '',
    notas: '',
  }), []);

  const startNew = () => {
    setEditingSupplier({ ...emptyForm });
    setShowForm(true);
  };

  const startEdit = (sup) => {
    setEditingSupplier({ id: sup.id, ...emptyForm, ...sup });
    setShowForm(true);
  };

  const saveSupplier = async (e) => {
    e?.preventDefault?.();
    if (!editingSupplier?.nombre) return;
    const data = {
      nombre: editingSupplier.nombre.trim(),
      contacto: (editingSupplier.contacto || '').trim(),
      telefono: (editingSupplier.telefono || '').trim(),
      email: (editingSupplier.email || '').trim(),
      nit: (editingSupplier.nit || '').trim(),
      direccion: (editingSupplier.direccion || '').trim(),
      notas: (editingSupplier.notas || '').trim(),
      updatedAt: serverTimestamp(),
    };
    if (editingSupplier.id) {
      await updateDoc(doc(db, 'proveedores', editingSupplier.id), data);
      await logAudit({ module: 'Proveedores', action: 'provider.updated', severity: 'warning', target: { id: editingSupplier.id, nombre: data.nombre }, details: { email: data.email, telefono: data.telefono } }, currentUser);
    } else {
      const payload = { ...data, createdAt: serverTimestamp(), active: true };
      const ref = await addDoc(collection(db, 'proveedores'), payload);
      await logAudit({ module: 'Proveedores', action: 'provider.created', severity: 'info', target: { id: ref.id, nombre: data.nombre }, details: { email: data.email, telefono: data.telefono } }, currentUser);
    }
    setShowForm(false);
    setEditingSupplier(null);
  };

  const deleteSupplier = async (sup) => {
    if (!sup?.id) return;
    if (!window.confirm(`¿Eliminar al proveedor “${sup.nombre}”? Esta acción no borra su historial y puedes restaurarlo luego.`)) return;
    // Borrado suave: active=false
    await updateDoc(doc(db, 'proveedores', sup.id), { active: false, deletedAt: serverTimestamp() });
    await logAudit({ module: 'Proveedores', action: 'provider.deleted', severity: 'critical', target: { id: sup.id, nombre: sup.nombre } }, currentUser);
  };

  const loadHistory = async (sup) => {
    setSelectedSupplier(sup);
    setLoadingHistory(true);
    try {
      // Prioriza supplierId; si no, usa supplier (nombre)
      let qRef = query(collection(db, 'entradas'), where('supplierId', '==', sup.id), orderBy('timestamp', 'desc'), limit(100));
      let snap = await getDocs(qRef);
      let rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (rows.length === 0) {
        qRef = query(collection(db, 'entradas'), where('supplier', '==', sup.nombre), orderBy('timestamp', 'desc'), limit(100));
        snap = await getDocs(qRef);
        rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
      setHistory(rows);
    } catch (e) {
      console.error('supplier history error', e);
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const sumPending = (list) => {
    return list
      .filter(r => (r.paymentStatus || '').toLowerCase() === 'pendiente')
      .reduce((s, r) => s + Number(r.totalEntryCost || 0), 0);
  };

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menú
        </button>
        <h1 className="text-4xl font-extrabold text-gray-900 drop-shadow-sm">Gestión de Proveedores</h1>
        <button
          onClick={startNew}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 shadow-md hover:shadow-lg flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nuevo Proveedor
        </button>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teléfono</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NIT</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dirección</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Deuda pendiente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {suppliers.length ? (
                suppliers.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{s.nombre}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.contacto || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.telefono || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.email || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.nit || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.direccion || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">${Number(debts[s.id] || 0).toLocaleString('es-CO')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                      <button onClick={() => startEdit(s)} className="text-blue-600 hover:text-blue-800">Editar</button>
                      <button onClick={() => deleteSupplier(s)} className="text-red-600 hover:text-red-800">Eliminar</button>
                      <button onClick={() => loadHistory(s)} className="text-green-600 hover:text-green-800">Histórico Compras</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">No hay proveedores registrados.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl w-full">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">{editingSupplier?.id ? 'Editar Proveedor' : 'Registrar Nuevo Proveedor'}</h2>
            <form onSubmit={saveSupplier}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Proveedor</label>
                  <input type="text" value={editingSupplier?.nombre || ''} onChange={(e) => setEditingSupplier(prev => ({ ...prev, nombre: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Persona de Contacto</label>
                  <input type="text" value={editingSupplier?.contacto || ''} onChange={(e) => setEditingSupplier(prev => ({ ...prev, contacto: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input type="text" value={editingSupplier?.telefono || ''} onChange={(e) => setEditingSupplier(prev => ({ ...prev, telefono: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={editingSupplier?.email || ''} onChange={(e) => setEditingSupplier(prev => ({ ...prev, email: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">NIT</label>
                  <input type="text" value={editingSupplier?.nit || ''} onChange={(e) => setEditingSupplier(prev => ({ ...prev, nit: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                  <input type="text" value={editingSupplier?.direccion || ''} onChange={(e) => setEditingSupplier(prev => ({ ...prev, direccion: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                  <textarea value={editingSupplier?.notas || ''} onChange={(e) => setEditingSupplier(prev => ({ ...prev, notas: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg min-h-[80px]" />
                </div>
              </div>
              <div className="mt-8 flex justify-end space-x-3">
                <button type="button" onClick={() => { setShowForm(false); setEditingSupplier(null); }} className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">Cancelar</button>
                <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Histórico */}
      {selectedSupplier && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-3xl w-full">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Histórico de Compras a {selectedSupplier.nombre}</h2>
            {loadingHistory ? (
              <p className="text-center text-gray-500">Cargando…</p>
            ) : history.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Factura</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado pago</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {history.map(h => (
                      <tr key={h.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{h.registrationDate || (h.timestamp?.toDate && h.timestamp.toDate().toLocaleDateString()) || '—'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{h.invoiceNumber || '—'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{h.paymentStatus || '—'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">${Number(h.totalEntryCost || 0).toLocaleString('es-CO')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-500">No hay compras registradas con este proveedor.</p>
            )}
            <div className="mt-6 flex justify-between items-center">
              <div className="text-sm text-gray-700">
                Deuda pendiente estimada: <strong>${sumPending(history).toLocaleString('es-CO')}</strong>
              </div>
              <button onClick={() => { setSelectedSupplier(null); setHistory([]); }} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierManagementView;
